import { FC, useMemo, useState, useEffect, useRef } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AppPage, AppCard } from '../../components/general';
import { BreadcrumbItem } from '../../components/general/AppPage';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import {
  IntegrationType,
  WaterfallFormModel,
  WaterfallViewModel,
  WaterfallVersionViewModel,
  StepViewModel,
  RefreshOrderSettingsViewModel,
  MultipleStepExecutionMode,
} from '../../models/waterfall';
import FormInputText from '../../components/form/FormInputText';
import FormDropdown from '../../components/form/FormDropdown';
import { classNames } from 'primereact/utils';
import { WaterfallPublicationsDialog } from './WaterfallPublicationsDialog';
import { useWaterfallFormSync } from './useWaterfallFormSync';
import { StepEditor } from './StepEditor';
import { RefreshOrderSettingsForm } from './RefreshOrderSettingsForm';
import { createEmptyStep, findAndUpdateStep, findAndDeleteStep, findAndReorderStep, cloneStep } from './stepUtils';

interface WaterfallEditorProps {
  waterfall: WaterfallViewModel | null;
  onSubmit: (data: Partial<WaterfallViewModel>) => Promise<void>;
  onCancel: () => void;
  onCreateVersion?: (sourceVersion: WaterfallVersionViewModel) => void;
  onSetActiveVersion?: (waterfallId: number, waterfallVersionId: number) => void;
  onSaveVersion?: (waterfall: WaterfallVersionViewModel) => void;
  onDeleteVersion?: (versionId: number) => Promise<void>;
  onToggleDisabled?: (waterfallId: number, disabled: boolean) => void;
  initialSelectedVersion?: number | null;
  versionIdsWithExecutionRecords?: number[];
  isLoadingExecutionCheck?: boolean;
  onOpenPublications?: () => void;
  allWaterfalls?: WaterfallViewModel[];
  defaultIntegrationType?: IntegrationType;
  basePath?: string;
}

const integrationTypeOptions = Object.values(IntegrationType).map((value) => ({ label: value, value }));

const basePathBreadcrumbMap: Record<string, { label: string; to: string }> = {
  '/credit/waterfalls': { label: 'Credit', to: '/products/credit' },
  '/fees/waterfalls': { label: 'Fees', to: '/products/fees' },
  '/vox/waterfalls': { label: 'VoX', to: '/products/vox' },
  '/auw/waterfalls': { label: 'AUW', to: '/products/auw' },
};

const buildEditorBreadcrumbs = (basePath: string, isEdit: boolean): BreadcrumbItem[] => {
  const parent = basePathBreadcrumbMap[basePath];
  if (parent) return [{ label: 'Dashboard', to: '/dashboard' }, parent, { label: 'Waterfalls', to: basePath }, { label: isEdit ? 'Edit' : 'Create' }];
  return [{ label: 'Dashboard', to: '/dashboard' }, { label: 'Waterfalls', to: basePath }, { label: isEdit ? 'Edit' : 'Create' }];
};

const validationSchema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required('Name is required')
    .max(50, 'Name must be 50 characters or less')
    .matches(/^[A-Za-z][A-Za-z0-9]*$/, 'Name must start with a letter and contain only alphanumeric characters'),
  description: yup.string().trim().required('Description is required').max(100, 'Description must be 100 characters or less'),
  integrationType: yup
    .mixed<IntegrationType>()
    .oneOf(Object.values(IntegrationType))
    .required('Integration type is required'),
  selectedVersion: yup.number().nullable(),
});

export const WaterfallEditor: FC<WaterfallEditorProps> = ({
  waterfall,
  onSubmit,
  onCancel,
  onCreateVersion,
  onSetActiveVersion,
  onSaveVersion,
  onDeleteVersion,
  onToggleDisabled,
  initialSelectedVersion,
  versionIdsWithExecutionRecords = [],
  isLoadingExecutionCheck = false,
  onOpenPublications,
  allWaterfalls = [],
  defaultIntegrationType,
  basePath = '/waterfalls',
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<WaterfallFormModel>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      name: waterfall?.name ?? '',
      description: waterfall?.description ?? '',
      integrationType: waterfall?.integrationType ?? defaultIntegrationType ?? IntegrationType.None,
      selectedVersion: waterfall?.currentVersionId ?? null,
    },
  });

  const waterfallForm = watch();
  const selectedVersion = watch('selectedVersion');

  const versionOptions =
    waterfall?.versions?.map((v) => ({
      label: `v${v.versionNumber}${v.waterfallVersionId === waterfall.currentVersionId ? ' (current version)' : ''}`,
      value: v.waterfallVersionId,
    })) ?? [];

  const handleFormSubmit = async (data: WaterfallFormModel) => {
    // Use ref to get the latest values (avoids stale closure issues with async state updates)
    const latestStepTrees = localInitialStepsRef.current;
    const latestRefreshSettings = localRefreshOrderSettingsRef.current;
    const latestExecutionMode = localInitialStepsExecutionModeRef.current;
    const latestParentDetail = localAlwaysSendParentDetailRef.current;

    try {
      if (onSaveVersion && currentVersion && latestStepTrees.length > 0) {
        await onSaveVersion({
          ...currentVersion,
          initialSteps: latestStepTrees,
          initialStepsExecutionMode: latestExecutionMode,
          refreshOrderSettings: latestRefreshSettings,
          alwaysSendParentWaterfallDetailInWebhookEvents: latestParentDetail,
        });
      } else {
        console.warn('Not saving version - onSaveVersion:', !!onSaveVersion, 'currentVersion:', !!currentVersion, 'latestStepTrees:', latestStepTrees.length);
      }

      await onSubmit({
        name: data.name,
        description: data.description,
        integrationType: waterfall?.integrationType ?? data.integrationType,
      });
    } catch (error) {
      // Error is already handled and toasted in the container
      // Just stop execution here - don't close the form
    }
  };

  const handleDuplicate = (version?: WaterfallVersionViewModel) => {
    if (!onCreateVersion || !waterfall) return;
    const source = version ?? waterfall.versions?.find((v) => v.waterfallVersionId === selectedVersion);
    if (source) onCreateVersion(source);
  };

  const handleActivate = (versionId?: number) => {
    const vid = versionId ?? selectedVersion;
    if (onSetActiveVersion && waterfall && vid) onSetActiveVersion(waterfall.waterfallId, vid);
  };

  const handleDeleteVersion = async (versionId: number) => {
    if (onDeleteVersion) await onDeleteVersion(versionId);
  };

  const [showPublications, setShowPublications] = useState(false);
  const isNameAutoGenerated = useRef(!waterfall);

  const handleOpenPublications = () => {
    onOpenPublications?.();
    setShowPublications(true);
  };

  useWaterfallFormSync({
    waterfall,
    initialSelectedVersion,
    reset,
    getValues,
    setValue,
  });

  const currentVersion = useMemo(() => {
    if (!waterfall || !selectedVersion) return null;
    return waterfall.versions?.find((v) => v.waterfallVersionId === selectedVersion) ?? null;
  }, [waterfall, selectedVersion]);

  // Local state for the step trees (UI only, no backend integration yet)
  const [localInitialSteps, setLocalInitialSteps] = useState<StepViewModel[]>([]);
  // Ref to track the latest step trees for immediate access (avoids stale closure issues)
  const localInitialStepsRef = useRef<StepViewModel[]>([]);

  // Local state for refresh order settings
  const [localRefreshOrderSettings, setLocalRefreshOrderSettings] = useState<RefreshOrderSettingsViewModel | null>(null);
  const localRefreshOrderSettingsRef = useRef<RefreshOrderSettingsViewModel | null>(null);

  // Local state for initial steps execution mode
  const [localInitialStepsExecutionMode, setLocalInitialStepsExecutionMode] = useState<MultipleStepExecutionMode>(MultipleStepExecutionMode.First);
  const localInitialStepsExecutionModeRef = useRef<MultipleStepExecutionMode>(MultipleStepExecutionMode.First);

  const [localAlwaysSendParentDetail, setLocalAlwaysSendParentDetail] = useState<boolean>(false);
  const localAlwaysSendParentDetailRef = useRef<boolean>(false);

  // Sync local step state and refresh order settings with current version
  // Only sync when the version ID changes, not when version data is refetched
  // This prevents resetting local changes when saving
  const previousVersionId = useRef<number | null>(null);
  useEffect(() => {
    const currentVersionId = currentVersion?.waterfallVersionId ?? null;
    
    // Only sync if the version ID actually changed (user selected different version)
    if (currentVersionId !== previousVersionId.current) {
      previousVersionId.current = currentVersionId;
      if (currentVersion?.initialSteps && currentVersion.initialSteps.length > 0) {
        const clonedSteps = currentVersion.initialSteps.map(step => cloneStep(step));

        localInitialStepsRef.current = clonedSteps; // Update ref immediately
        setLocalInitialSteps(clonedSteps);
      } else {
        localInitialStepsRef.current = []; // Update ref immediately
        setLocalInitialSteps([]);
      }
      
      // Sync refresh order settings
      const refreshSettings = currentVersion?.refreshOrderSettings ?? null;
      localRefreshOrderSettingsRef.current = refreshSettings;
      setLocalRefreshOrderSettings(refreshSettings);

      // Sync initial steps execution mode
      const executionMode = currentVersion?.initialStepsExecutionMode ?? MultipleStepExecutionMode.First;
      localInitialStepsExecutionModeRef.current = executionMode;
      setLocalInitialStepsExecutionMode(executionMode);

      // Sync alwaysSendParentWaterfallDetailInWebhookEvents
      const parentDetailSetting = currentVersion?.alwaysSendParentWaterfallDetailInWebhookEvents ?? false;
      localAlwaysSendParentDetailRef.current = parentDetailSetting;
      setLocalAlwaysSendParentDetail(parentDetailSetting);
    }
  }, [currentVersion]);

  // Check if there are changes to waterfall metadata
  const hasMetadataDiff =
    waterfall && (waterfall.name !== waterfallForm.name || waterfall.description !== waterfallForm.description || waterfall.integrationType !== waterfallForm.integrationType);

  // Check if there are changes to the step structure
  const hasStepDiff = useMemo(() => {
    if (!currentVersion) return false;
    const currentInitialSteps = currentVersion.initialSteps ?? [];
    return JSON.stringify(currentInitialSteps) !== JSON.stringify(localInitialSteps);
  }, [currentVersion, localInitialSteps]);

  // Check if there are changes to refresh order settings
  const hasRefreshSettingsDiff = useMemo(() => {
    if (!currentVersion) return false;
    return JSON.stringify(currentVersion.refreshOrderSettings ?? null) !== JSON.stringify(localRefreshOrderSettings);
  }, [currentVersion, localRefreshOrderSettings]);

  // Check if there are changes to initial steps execution mode
  const hasExecutionModeDiff = useMemo(() => {
    if (!currentVersion) return false;
    return currentVersion.initialStepsExecutionMode !== localInitialStepsExecutionMode;
  }, [currentVersion, localInitialStepsExecutionMode]);

  const hasParentDetailDiff = useMemo(() => {
    if (!currentVersion) return false;
    return (currentVersion.alwaysSendParentWaterfallDetailInWebhookEvents ?? false) !== localAlwaysSendParentDetail;
  }, [currentVersion, localAlwaysSendParentDetail]);

  const hasDiff = hasMetadataDiff || hasStepDiff || hasRefreshSettingsDiff || hasExecutionModeDiff || hasParentDetailDiff;

  // For create: disable if name or integrationType is missing
  // For edit: disable if no changes
  const disableFormSave = isSubmitting || 
    (!waterfall && (!waterfallForm.name?.trim() || !waterfallForm.description?.trim() || !waterfallForm.integrationType || waterfallForm.integrationType === IntegrationType.None)) ||
    !!(waterfall && !hasDiff);

  const handleUpdateStep = (updatedStep: StepViewModel) => {
    const newStepTrees = localInitialSteps.map(step => cloneStep(step));
    
    let found = false;
    for (const tree of newStepTrees) {
      found = findAndUpdateStep(tree, updatedStep.stepGuid, (step) => {
        // Preserve nextSteps (tree structure) and stepGuid, but replace all other properties
        const nextSteps = step.nextSteps;
        const stepGuid = step.stepGuid;
        
        // Replace all properties from updatedStep
        Object.assign(step, updatedStep);
        
        // Restore tree structure properties
        step.nextSteps = nextSteps;
        step.stepGuid = stepGuid;
      });
      if (found) break;
    }

    if (!found) {
      console.warn('Step not found in trees:', updatedStep.stepGuid);
      return;
    }
    localInitialStepsRef.current = newStepTrees; // Update ref immediately
    setLocalInitialSteps(newStepTrees); // Update state (async)
  };

  const handleAddStep = (parentStep: StepViewModel) => {
    const newStep = createEmptyStep();
    const newStepTrees = localInitialSteps.map(step => cloneStep(step));

    for (const tree of newStepTrees) {
      const found = findAndUpdateStep(tree, parentStep.stepGuid, (step) => {
        if (!step.nextSteps) {
          step.nextSteps = [newStep];
        } else {
          step.nextSteps.push(newStep);
        }
      });
      if (found) break;
    }

    localInitialStepsRef.current = newStepTrees; // Update ref immediately
    setLocalInitialSteps(newStepTrees);
  };

  const handleDeleteStep = (stepToDelete: StepViewModel) => {
    const newStepTrees = localInitialSteps.map(step => cloneStep(step));
    
    let deleted = false;
    for (const tree of newStepTrees) {
      deleted = findAndDeleteStep(tree, stepToDelete.stepGuid);
      if (deleted) break;
    }

    if (deleted) {
      localInitialStepsRef.current = newStepTrees; // Update ref immediately
      setLocalInitialSteps(newStepTrees);
    }
  };

  const handleAddInitialStep = () => {
    const newStep = createEmptyStep(true);
    const newStepTrees = [...localInitialSteps, newStep];
    
    localInitialStepsRef.current = newStepTrees;
    setLocalInitialSteps(newStepTrees);
  };

  const handleDeleteInitialStep = (stepGuid: string) => {
    const newStepTrees = localInitialSteps.filter(step => step.stepGuid !== stepGuid);

    localInitialStepsRef.current = newStepTrees;
    setLocalInitialSteps(newStepTrees);
  };

  const handleReorderStep = (stepToReorder: StepViewModel, direction: 'up' | 'down') => {
    const newStepTrees = localInitialSteps.map(step => cloneStep(step));

    let reordered = false;
    for (const tree of newStepTrees) {
      reordered = findAndReorderStep(tree, stepToReorder.stepGuid, direction);
      if (reordered) break;
    }

    if (reordered) {
      localInitialStepsRef.current = newStepTrees;
      setLocalInitialSteps(newStepTrees);
    }
  };

  const handleRefreshOrderSettingsChange = (settings: RefreshOrderSettingsViewModel) => {
    localRefreshOrderSettingsRef.current = settings;
    setLocalRefreshOrderSettings(settings);
  };

  const handleInitialStepsExecutionModeChange = (mode: MultipleStepExecutionMode) => {
    localInitialStepsExecutionModeRef.current = mode;
    setLocalInitialStepsExecutionMode(mode);
  };

  const handleAlwaysSendParentDetailChange = (value: boolean) => {
    localAlwaysSendParentDetailRef.current = value;
    setLocalAlwaysSendParentDetail(value);
  };

  return (
    <AppPage title={waterfall ? '' : 'Create Waterfall'} breadcrumbs={buildEditorBreadcrumbs(basePath, !!waterfall)}>
      {waterfall && (
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold m-0">Edit Waterfall</h1>
            <Tag value={waterfall.integrationType} severity="info" />
          </div>
          <div className="flex items-center gap-2">
            <Tag
              value={waterfall.disabled ? 'Disabled' : 'Enabled'}
              severity={waterfall.disabled ? 'secondary' : 'success'}
            />
            <Button
              type="button"
              label={waterfall.disabled ? 'Enable Waterfall' : 'Disable Waterfall'}
              icon={waterfall.disabled ? 'pi pi-check-circle' : 'pi pi-ban'}
              className={`p-button-sm ${waterfall.disabled ? 'p-button-success' : 'p-button-danger p-button-outlined'}`}
              onClick={() => onToggleDisabled?.(waterfall.waterfallId, !waterfall.disabled)}
            />
          </div>
        </div>
      )}
      <AppCard>
        <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className="p-fluid p-input-filled">
          <div className="grid">
            <div className={`col-12 ${waterfall ? 'md:col-4' : 'md:col-6'}`}>
              <FormInputText
                control={control}
                errors={errors}
                field="description"
                label="Description"
                placeholder="Enter Waterfall Description"
                required
                maxLength={100}
                onChange={(e) => {
                  const desc = e.target.value;
                  const currentName = getValues('name');
                  if (!currentName || isNameAutoGenerated.current) {
                    setValue('name', desc.replace(/[^A-Za-z0-9]/g, '').replace(/^[0-9]+/, '').substring(0, 50), { shouldValidate: true });
                    isNameAutoGenerated.current = true;
                  }
                }}
              />
            </div>
            <div className={`col-12 ${waterfall ? 'md:col-4' : 'md:col-6'}`}>
              <FormInputText
                control={control}
                errors={errors}
                field="name"
                label="Name"
                placeholder="Enter Waterfall Name"
                required
                maxLength={50}
                onChange={() => {
                  isNameAutoGenerated.current = false;
                }}
              />
            </div>
            {!waterfall && (
              <div className="col-12 md:col-6">
                <FormDropdown
                  control={control}
                  errors={errors}
                  field="integrationType"
                  label="Integration Type"
                  options={integrationTypeOptions}
                  placeholder="Select Type"
                  required
                />
              </div>
            )}
            {waterfall && (
              <div className="col-12 md:col-4">
                <FormDropdown
                  control={control}
                  errors={errors}
                  field="selectedVersion"
                  label="Select Version"
                  options={versionOptions}
                  placeholder="Select Version"
                />
                <div className="mt-2">
                  <Button
                    icon="pi pi-list"
                    label="Publications"
                    className="p-button-sm p-button-outlined"
                    type="button"
                    onClick={handleOpenPublications}
                  />
                </div>
              </div>
            )}
          </div>

          {waterfall && currentVersion && (
            <div className="mb-5">
              <StepEditor
                initialSteps={localInitialSteps}
                onUpdateStep={handleUpdateStep}
                onAddStep={handleAddStep}
                onDeleteStep={handleDeleteStep}
                onReorderStep={handleReorderStep}
                onAddInitialStep={handleAddInitialStep}
                onDeleteInitialStep={handleDeleteInitialStep}
                initialStepsExecutionMode={localInitialStepsExecutionMode}
                onInitialStepsExecutionModeChange={handleInitialStepsExecutionModeChange}
                integrationType={waterfall.integrationType}
              />
            </div>
          )}

          {waterfall && currentVersion && (
            <div className="mb-5">
              <RefreshOrderSettingsForm
                settings={localRefreshOrderSettings}
                onChange={handleRefreshOrderSettingsChange}
                waterfalls={allWaterfalls}
                disabled={isSubmitting}
                alwaysSendParentWaterfallDetailInWebhookEvents={localAlwaysSendParentDetail}
                onAlwaysSendParentDetailChange={handleAlwaysSendParentDetailChange}
              />
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined p-button-sm"
              onClick={onCancel}
              disabled={isSubmitting}
            />
            <Button
              className={classNames({ 'opacity-50': disableFormSave }, 'p-button-primary p-button-sm')}
              type="submit"
              label={waterfall ? 'Save' : 'Create'}
              loading={isSubmitting}
              disabled={disableFormSave}
            />
          </div>
        </form>
      </AppCard>

      {waterfall && (
        <WaterfallPublicationsDialog
          visible={showPublications}
          onClose={() => setShowPublications(false)}
          versions={waterfall.versions ?? []}
          currentVersionId={waterfall.currentVersionId}
          onDuplicate={handleDuplicate}
          onSetActive={(versionId) => handleActivate(versionId)}
          onDelete={handleDeleteVersion}
          versionIdsWithExecutionRecords={versionIdsWithExecutionRecords}
          isLoadingExecutionCheck={isLoadingExecutionCheck}
        />
      )}
    </AppPage>
  );
};
